import { zodResolver } from '@hookform/resolvers/zod';
import { authErrorMessage, RequestPasswordResetInput } from '@myhome/contracts';
import { Button } from '@myhome/ui/components/button';
import { Form } from '@myhome/ui/components/form';
import { SpinnerGap } from '@myhome/ui/icons';
import { StatusBanner } from '@myhome/ui/patterns/status-banner';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { AuthScreen, EmailField, FormError } from '../components/auth-screen';
import { authClient } from '../lib/auth-client';

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
});

/**
 * Asks for a reset link aimed at /reset-password. Never /set-password: the
 * API words a reset aimed there as an admin invitation (auth.md, Admin invite).
 */
function ForgotPasswordPage() {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const form = useForm({
    resolver: zodResolver(RequestPasswordResetInput),
    defaultValues: { email: '' },
    mode: 'onBlur',
  });

  const onSubmit = form.handleSubmit(async ({ email }) => {
    const { error } = await authClient.requestPasswordReset({
      email,
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error !== null) {
      form.setError('root', { message: authErrorMessage(error) });
      return;
    }
    setSentTo(email);
  });

  const { isSubmitting, errors } = form.formState;

  return (
    <AuthScreen title="Reset your password" description="We will email you a link to choose a new password.">
      {sentTo === null ? (
        <Form {...form}>
          <form noValidate onSubmit={onSubmit} className="flex flex-col gap-3">
            <FormError message={errors.root?.message} />
            <EmailField control={form.control} name="email" label="Email address" />
            <Button type="submit" block disabled={isSubmitting}>
              {isSubmitting ? (
                <SpinnerGap className="animate-spin motion-reduce:animate-none" aria-hidden="true" />
              ) : null}
              Send me a reset link
            </Button>
          </form>
        </Form>
      ) : (
        // The same words whether or not the address has an account.
        <StatusBanner
          tone="success"
          title="Check your email"
          description={
            <>
              If <span className="font-semibold break-all">{sentTo}</span> has an account, we have sent it a
              link. The link lasts 1 hour.
            </>
          }
        />
      )}
      <Button asChild variant="link" className="min-h-(--spacing-touch) self-start">
        <Link to="/login">Back to sign in</Link>
      </Button>
    </AuthScreen>
  );
}
